import React, { useRef, useState } from "react";
import { UploadCloud, X, File as FileIcon, RotateCw, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { toast } from "sonner";

// Bug #8 — Upload avec feedback complet : progress, success, error + retry.
// Chaque fichier a un état individuel `queue` : { id, name, size, progress, status, error, upload }.

export default function FileUploader({ files, setFiles, category = "client_upload", dark = false, testId = "uploader" }) {
  const inputRef = useRef();
  const [queue, setQueue] = useState([]); // uploads en cours ou en erreur
  const [drag, setDrag] = useState(false);

  const startUpload = (localId, nativeFile) => {
    const fd = new FormData();
    fd.append("file", nativeFile);
    fd.append("category", category);
    return api.post("/files/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (!evt.total) return;
        const pct = Math.round((evt.loaded * 100) / evt.total);
        setQueue((q) => q.map((it) => (it.localId === localId ? { ...it, progress: pct } : it)));
      },
    });
  };

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    const items = Array.from(fileList).map((f) => ({
      localId: `${Date.now()}_${f.name}_${Math.random().toString(36).slice(2, 8)}`,
      name: f.name,
      size: f.size,
      progress: 0,
      status: "uploading",
      error: null,
      nativeFile: f,
    }));
    setQueue((q) => [...q, ...items]);

    for (const item of items) {
      try {
        const { data } = await startUpload(item.localId, item.nativeFile);
        setQueue((q) => q.map((it) => (it.localId === item.localId ? { ...it, status: "done", progress: 100 } : it)));
        setFiles((prev) => [...prev, data]);
        // Retirer du queue après un court délai visuel
        setTimeout(() => {
          setQueue((q) => q.filter((it) => it.localId !== item.localId));
        }, 800);
      } catch (e) {
        const msg = formatApiError(e);
        setQueue((q) => q.map((it) => (it.localId === item.localId ? { ...it, status: "error", error: msg } : it)));
        toast.error(`${item.name} — ${msg}`);
      }
    }
  };

  const retry = async (localId) => {
    const item = queue.find((it) => it.localId === localId);
    if (!item) return;
    setQueue((q) => q.map((it) => (it.localId === localId ? { ...it, status: "uploading", progress: 0, error: null } : it)));
    try {
      const { data } = await startUpload(localId, item.nativeFile);
      setQueue((q) => q.map((it) => (it.localId === localId ? { ...it, status: "done", progress: 100 } : it)));
      setFiles((prev) => [...prev, data]);
      setTimeout(() => setQueue((q) => q.filter((it) => it.localId !== localId)), 800);
      toast.success(`${item.name} — envoyé`);
    } catch (e) {
      const msg = formatApiError(e);
      setQueue((q) => q.map((it) => (it.localId === localId ? { ...it, status: "error", error: msg } : it)));
      toast.error(`${item.name} — ${msg}`);
    }
  };

  const cancelQueued = (localId) => setQueue((q) => q.filter((it) => it.localId !== localId));

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    upload(e.dataTransfer.files);
  };

  const remove = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const border = dark ? "border-white/25 hover:border-[#0E7C90]" : "border-black/25 hover:border-[#0E7C90]";
  const text = dark ? "text-neutral-300" : "text-neutral-600";

  return (
    <div data-testid={testId}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed ${border} ${drag ? "border-[#0E7C90] bg-[#0E7C90]/5" : ""} p-8 text-center transition-colors`}
      >
        <UploadCloud size={28} className={`mx-auto ${dark ? "text-neutral-300" : "text-neutral-600"}`} />
        <div className={`mt-3 text-sm ${text}`}>
          Glissez vos fichiers ou <span className="underline">parcourez</span>
        </div>
        <div className="mt-2 text-xs font-mono text-neutral-500">PDF · JPEG · PNG · TIFF · PSD · DOCX · ZIP — max 150 Mo</div>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid={`${testId}-input`} />
      </div>

      {/* File in queue : uploading / error */}
      {queue.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid={`${testId}-queue`}>
          {queue.map((it) => (
            <li key={it.localId}
                className={`px-3 py-2 border ${it.status === "error" ? "border-red-400 bg-red-50" : dark ? "border-white/20 bg-white/5" : "border-black/15 bg-white"}`}
                data-testid={`${testId}-queue-${it.status}`}>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  {it.status === "uploading" && <Loader2 size={14} className="animate-spin text-[#0E7C90] shrink-0" />}
                  {it.status === "done" && <CheckCircle2 size={14} className="text-[#0E7C90] shrink-0" />}
                  {it.status === "error" && <AlertTriangle size={14} className="text-red-600 shrink-0" />}
                  <span className="truncate">{it.name}</span>
                  <span className="font-mono text-xs text-neutral-500 shrink-0">{Math.round((it.size || 0) / 1024)} Ko</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {it.status === "uploading" && (
                    <span className="font-mono text-xs text-neutral-600">{it.progress}%</span>
                  )}
                  {it.status === "error" && (
                    <>
                      <button type="button" onClick={() => retry(it.localId)}
                              data-testid={`${testId}-retry-${it.localId}`}
                              className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[#0E7C90] hover:underline">
                        <RotateCw size={12} /> Réessayer
                      </button>
                      <button type="button" onClick={() => cancelQueued(it.localId)}
                              data-testid={`${testId}-cancel-${it.localId}`}
                              className="opacity-70 hover:opacity-100">
                        <X size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {it.status === "uploading" && (
                <div className="mt-2 h-1 w-full bg-black/10 overflow-hidden">
                  <div className="h-full bg-[#0E7C90] transition-[width] duration-150" style={{ width: `${it.progress}%` }} />
                </div>
              )}
              {it.status === "error" && (
                <div role="alert" aria-live="polite" className="mt-1 text-[12px] font-mono text-red-700">
                  {it.error}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {files.length > 0 && (
        <ul className="mt-4 space-y-2">
          {files.map((f) => (
            <li key={f.id} className={`flex items-center justify-between px-3 py-2 border ${dark ? "border-white/20" : "border-black/15 bg-white"}`}>
              <div className="flex items-center gap-3 text-sm min-w-0">
                <FileIcon size={16} className="shrink-0" />
                <span className="truncate">{f.original_name}</span>
                <span className="font-mono text-xs text-neutral-500 shrink-0">{Math.round((f.size || 0) / 1024)} Ko</span>
              </div>
              <button type="button" onClick={() => remove(f.id)} className="ml-3 opacity-70 hover:opacity-100" data-testid={`${testId}-remove-${f.id}`}>
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
